import axios from 'axios';

import { parameters } from '../../constants/parameters';
import { Test } from '../../entities/test';
import { OrderView } from '../../entities/order-view';
import { AllergensAnswer } from '../../entities/allergens-answer';
import { FullAnswer } from '../../entities/full-answer';
import { Recommendation } from '../../entities/recommendation';

interface TestsListResponse {
  tests: Test[];
  total: number;
  limit: number;
  offset: number;
}

export const list = async (
  token: string,
  params?: {
    status?: string;
    administratorId?: number | string;
    productId?: number;
    siteId?: number;
    countryId?: number;
    patientHash?: string;
    excludeTestId?: number;
    textSearch?: string;
    questionnaireFilled?: string;
    limit?: number;
    offset?: number;
  },
): Promise<TestsListResponse> => {
  const url = `${parameters.api.baseUrl}/tests`;

  // Filter out undefined values to prevent axios from sending them as "undefined" strings
  const cleanParams = Object.entries(params || {}).reduce((acc, [key, value]) => {
    if (value !== undefined && value !== null && !Number.isNaN(value)) {
      acc[key] = value;
    }

    return acc;
  }, {} as Record<string, unknown>);

  try {
    const res = await axios.get(url, {
      headers: { Authorization: `Bearer ${token}` },
      params: cleanParams,
    });

    return res.data;
  } catch (e) {
    if (axios.isAxiosError(e)) {
      if (e.response?.data.error) {
        throw new Error(e.response?.data.error);
      }
    }

    throw e;
  }
};

export const view = async (token: string, id: number): Promise<OrderView & { test: Test }> => {
  const url = `${parameters.api.baseUrl}/tests/${id}`;

  const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
};

export const update = async (token: string, id: number, data: Partial<Test>): Promise<{ test: Test }> => {
  const url = `${parameters.api.baseUrl}/tests/${id}`;

  const res = await axios.patch(url, data, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
};

export const complete = async (token: string, id: number): Promise<{ test: Test }> => {
  const url = `${parameters.api.baseUrl}/tests/${id}/complete`;

  const res = await axios.post(url, { id }, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
};

export const cancel = async (token: string, id: number): Promise<{ test: Test }> => {
  const url = `${parameters.api.baseUrl}/tests/${id}/cancel`;

  const res = await axios.post(url, { id }, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
};

export const manualImport = async (token: string, id: number, remoteId: number): Promise<unknown> => {
  const url = `${parameters.api.baseUrl}/tests/imunodia/manual-import`;

  const res = await axios.post(url, { id, remoteId }, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
};

export const result = async (token: string, testId: number): Promise<FullAnswer> => {
  const url = `${parameters.api.baseUrl}/tests/result/${testId}`;

  const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
};

export const getAllergens = async (token: string, testId: number): Promise<AllergensAnswer> => {
  const url = `${parameters.api.baseUrl}/tests/allergens/${testId}`;

  const res = await axios.get(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  return res.data;
};

export const updateRecommendations = async (
  token: string,
  orderId: number,
  recommendations: Recommendation[],
): Promise<{ status: string }> => {
  const url = `${parameters.api.baseUrl}/orders/update-recommendations`;

  const res = await axios.post(
    url,
    {
      orderId,
      recommendations,
    },
    { headers: { Authorization: `Bearer ${token}` } },
  );

  return res.data;
};

export interface PatientOrder {
  id: number;
  status: string;
  createdAt: string;
  email: string;
  productKey?: string;
  productTitle?: string;
}

export const getPatientOrders = async (
  token: string,
  patientId: number,
  excludeOrderId: number,
): Promise<{ orders: PatientOrder[] }> => {
  const url = `${parameters.api.baseUrl}/tests/patient-orders/${patientId}/${excludeOrderId}`;

  const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });

  return res.data;
};
